// GeoJSON Point returned by backend geo_location field
interface GeoPoint {
  type: 'Point'
  coordinates: [number, number] // [longitude, latitude]
}

// --- Entity Types ---

export type CustomEntityType = 'person' | 'company' | 'supplier' | 'seller' | 'webshop' | 'importer' | 'exporter' | 'other'

export interface CustomEntity {
  id: string
  name: string
  nickname: string | null
  description: string | null
  type: CustomEntityType
  url: string | null
  seizures: string[]
  brands: string[]
  email: string | null
  phone: string | null
  product_types: string[]
  geo_location: GeoPoint | null
  address: string | null
  city: string | null
  postcode: string | null
  country: string | null
  tax_id: string | null
  details: Record<string, unknown>
  language: string | null
  address_type: string | null
  created_at: string
  updated_at: string
  // Direct links to Ocean objects (ids) + display data from the serializer.
  items?: string[]
  sellers?: string[]
  saws?: string[]
  linked_objects?: EntityLinkedObjects
}

export interface EntityLinkedObjects {
  items: Array<{ id: string; title: string; url?: string | null }>
  sellers: Array<{ id: string; name: string }>
  saws: Array<{ id: string; domain: string }>
}

export interface CreateEntityPayload {
  name: string
  nickname?: string
  description?: string
  type?: CustomEntityType
  url?: string
  email?: string
  phone?: string
  product_types?: string[]
  address?: string
  city?: string
  postcode?: string
  country?: string
  tax_id?: string
  details?: Record<string, unknown>
  seizures?: string[]
  brands?: string[]
  items?: string[]
  sellers?: string[]
  saws?: string[]
  related_entities?: string[]
  language?: string
  address_type?: string
}

/** Pre-link info passed when creating an entity from a graph node. */
export interface EntityPreLink {
  field: 'items' | 'sellers' | 'saws' | 'seizures' | 'related_entities'
  id: string
  label: string
}

export interface UpdateEntityPayload extends Partial<CreateEntityPayload> {}

// --- Linked entity summaries (used inside CustomSeizure) ---

export interface LinkedEntity {
  id: string
  name: string
  type: CustomEntityType
  nickname: string | null
}

export interface LinkedProduct {
  id: string
  name: string
  product_type: string
  photo: string | null
  price: string
}

export interface LinkedItem {
  id: string
  title: string
  status: string
  thumbnail: string | null
  url: string | null
}

export interface LinkedSeller {
  id: string
  name: string
  url: string | null
  country: string | null
}

// --- Seizure Types ---

export interface CustomSeizure {
  id: string
  name: string | null
  details: Record<string, unknown>
  brands: string[]
  geo_location: GeoPoint | null
  address: string | null
  city: string | null
  postcode: string | null
  country: string | null
  value: string | null
  product_types: string[]
  seized_at: string | null
  remarks: string | null
  linked_entities: LinkedEntity[]
  linked_products: LinkedProduct[]
  linked_items: LinkedItem[]
  linked_sellers: LinkedSeller[]
  created_by_name: string | null
  created_by_email: string | null
  created_at: string
  updated_at: string
}

export interface CreateSeizurePayload {
  name?: string
  details?: Record<string, unknown>
  address?: string
  city?: string
  postcode?: string
  country?: string
  value?: number
  product_types?: string[]
  seized_at?: string
  remarks?: string
  brands?: string[]
}

export interface UpdateSeizurePayload extends Partial<CreateSeizurePayload> {}

// Activity log entry for a seizure (from UserActionSerializer)
export interface SeizureActivity {
  id: string
  user: string | null
  user_email: string | null
  user_name: string | null
  action: 'create' | 'update' | 'delete' | 'custom'
  model_name: string
  object_id: string
  changes: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  timestamp: string
}

// --- Product Types ---

export interface CustomProduct {
  id: string
  name: string | null
  details: Record<string, unknown>
  photo: string | null
  price: string | null
  product_type: string | null
  url: string | null
  reference: string | null
  seller: string | null
  website: string | null
  seizures: string[]
  brands: string[]
  created_at: string
  updated_at: string
}

export interface CreateProductPayload {
  name?: string
  details?: Record<string, unknown>
  price?: number
  product_type?: string
  url?: string
  reference?: string
  seller?: string | null
  website?: string | null
  seizures?: string[]
  brands?: string[]
}

export interface UpdateProductPayload extends Partial<CreateProductPayload> {}

// --- Paginated list response ---

export interface CustomEntityListResponse {
  data: CustomEntity[]
  count: number
  total_amount: number
  hasNext: boolean
  nextPage: number | null
  totalPages: number
  success: boolean
}

export interface CustomSeizureListResponse {
  data: CustomSeizure[]
  count: number
  total_amount: number
  hasNext: boolean
  nextPage: number | null
  totalPages: number
  success: boolean
}

export interface CustomProductListResponse {
  data: CustomProduct[]
  count: number
  total_amount: number
  hasNext: boolean
  nextPage: number | null
  totalPages: number
  success: boolean
}

// --- UI constants ---

export const entityTypeLabels: Record<CustomEntityType, string> = {
  person: 'Person',
  company: 'Company',
  supplier: 'Supplier',
  seller: 'Seller',
  webshop: 'Webshop',
  importer: 'Importer',
  exporter: 'Exporter',
  other: 'Other',
}

export type LinkSearchType = 'entity' | 'product' | 'item' | 'seller' | 'saw'

export const entityTypeColors: Record<CustomEntityType, { bg: string; color: string; scheme: string }> = {
  person: { bg: 'rgba(79, 70, 229, 0.10)', color: '#4F46E5', scheme: 'purple' },
  company: { bg: 'rgba(14, 165, 229, 0.10)', color: '#0EA5E9', scheme: 'blue' },
  supplier: { bg: 'rgba(245, 158, 11, 0.10)', color: '#F59E0B', scheme: 'orange' },
  seller: { bg: 'rgba(16, 185, 129, 0.10)', color: '#10B981', scheme: 'green' },
  webshop: { bg: 'rgba(236, 72, 153, 0.10)', color: '#EC4899', scheme: 'pink' },
  importer: { bg: 'rgba(168, 85, 247, 0.10)', color: '#A855F7', scheme: 'purple' },
  exporter: { bg: 'rgba(20, 184, 166, 0.10)', color: '#14B8A6', scheme: 'teal' },
  other: { bg: 'rgba(107, 114, 128, 0.10)', color: '#6B7280', scheme: 'gray' },
}

// --- Stats Params & Response (for overview page) ---

export interface CustomEntitiesStatsParams {
  from_date: string
  to_date: string
  brands?: string
}


export interface MapMarker {
  id: string
  name: string
  type: 'seizure' | 'entity'
  entity_type?: string
  country: string
  lat: number
  lng: number
}

export interface CustomEntitiesStats {
  total_seizures: number
  total_entities: number
  total_products: number
  total_value: number
  map_data: { name: string; items: number }[]
  map_markers: MapMarker[]
  time_series: { name: string; seizures: number; value: number }[]
  entity_type_breakdown: { type: string; count: number }[]
  product_type_breakdown: { product_type: string; count: number }[]
  top_countries: { country: string; count: number }[]
  avg_entities_per_seizure: number
  filters: { from_date: string; to_date: string }
}
